import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Box, Button, Divider, SxProps, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsIconOutlined from '@mui/icons-material/SettingsOutlined';
import { useDefaultsForm } from './hooks/useDefaultsForm';
import { useService } from '../../../contexts/ServiceContext';
import { MemberDefaults } from '../../../system/defaults';
import { useEditableFieldForm } from '../hooks/useEditableFieldForm/useEditableFieldForm';
import { FeatureFlags } from '../../../flags';
import { useFeatureFlags } from 'src/contexts/FeatureFlagsContext';
import { useDefaults } from '../../../contexts/DefaultsContext';
import { useNavPanelPosition } from './hooks/useNavPanelPosition';

const Header: React.FC = () => {
  return (
    <Box sx={{ pt: 2 }}>
      <Typography sx={{ mb: 2 }}>
        Set listing defaults to save time by populating frequently entered
        information.
      </Typography>
    </Box>
  );
};

interface SetFieldDefaultsProps {
  styles: SxProps;
  showDivider?: boolean;
  isMobile?: boolean;
  outlinedIcon?: boolean;
  content?: string | ReactElement;
}

const defaultLabel = 'Set Field Defaults';

const SetFieldDefaults: React.FC<SetFieldDefaultsProps> = ({
  styles,
  showDivider = true,
  isMobile = false,
  outlinedIcon = false,
  content = defaultLabel,
}) => {
  const { openEditableFieldForm } = useEditableFieldForm();

  const { form } = useDefaultsForm();
  const { defaults, setContextDefaults } = useDefaults();
  const { saveMemberDefaults, deleteMemberDefaults } = useService();
  const { hasFeature } = useFeatureFlags();
  const hasSidepanelRedesign = hasFeature(
    FeatureFlags.NGW_EDIT_SIDEPANEL_REDESIGN,
  );
  const navPosition = useNavPanelPosition();

  const [formData, setFormData] = useState<MemberDefaults>({});
  const [defaultsFieldNames, setDefaultsFieldNames] = useState<string[]>([]);

  const testIdLabel = typeof content === 'string' ? content : defaultLabel;

  const sidePanelStyles = {
    width: isMobile ? '100%' : navPosition.width,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: navPosition.bottom ? 'absolute' : 'fixed',
    left: isMobile ? 0 : navPosition.left,
    mt: '50px',
    bottom: 0,
    transition: 'bottom .25s ease-in 0s',
  };

  const createPageStyles = {};

  useEffect(() => {
    setDefaultsFieldNames(form.map((field) => field.Key));
  }, [form]);

  useEffect(() => {
    // get defaults values from BE - will be the same as in Legacy
    const intiDefaultValues = async () => {
      const res = await defaults();
      setFormData(res);
    };
    intiDefaultValues();
  }, [defaults, setFormData]);

  const pruneNullValues = useCallback(
    (defaults: MemberDefaults): MemberDefaults => {
      const toBeDeleted: MemberDefaults = {};

      Object.entries(defaults).forEach((entry) => {
        const [key, value] = entry;

        const shouldDeleteKey =
          value === null ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && !value.length);

        if (shouldDeleteKey) {
          toBeDeleted[key] = value;
          delete defaults[key];
        }
      });
      if (Object.keys(toBeDeleted).length) {
        deleteMemberDefaults(toBeDeleted);
      }

      return defaults;
    },
    [deleteMemberDefaults],
  );

  const normalizeNumberToBool = (defaults: MemberDefaults): MemberDefaults => {
    Object.keys(defaults).forEach((key) => {
      const value = defaults[key];
      if (key.endsWith('YN')) {
        if (value === 0) {
          defaults[key] = false;
        }
        if (value === 1) {
          defaults[key] = true;
        }
      }
    });

    return defaults;
  };

  const handleOpenDefaults = useCallback(() => {
    openEditableFieldForm(
      {
        fields: form,
        formHeader: <Header />,
      },
      {
        title: 'Set Field Defaults',
      },
      (record: any) => {
        const defaults: MemberDefaults = {};
        defaultsFieldNames.forEach((name) => {
          // hack to avoid overwriting CO_LIST_AGENT_MLS_ID with old data from DefaultsContext
          // Because we can't access context from CoListAgent, the needed data will always be missing from the record
          // CO_LIST_AGENT data is set in AgentFormDefault instead, for now
          // to do: clean this up when we implement zustand defaults store
          if (name === 'CO_LIST_AGENT_MLS_ID') return;
          defaults[name] = record[name];
        });

        // deletion is handled through a separate endpoint, so first we trim
        // any defaults with null values
        const prunedDefaults = pruneNullValues(defaults);
        const normalizedBoolDefaults = normalizeNumberToBool(prunedDefaults);
        setFormData(normalizedBoolDefaults);

        // finally, we save all non-null
        setContextDefaults(normalizedBoolDefaults); // sets defaults on context to keep it in sync across pages
        saveMemberDefaults(normalizedBoolDefaults); // sets defaults in the BE for storage
      },
      { ...formData },
    );
  }, [
    form,
    defaultsFieldNames,
    pruneNullValues,
    openEditableFieldForm,
    formData,
    saveMemberDefaults,
  ]);

  return (
    <Box sx={showDivider ? sidePanelStyles : createPageStyles}>
      {hasSidepanelRedesign && showDivider && (
        <Divider
          sx={{
            borderWidth: '1px',
            width: '80%',
            margin: '0 auto',
          }}
        />
      )}
      <Button
        startIcon={
          hasSidepanelRedesign && outlinedIcon ? (
            <SettingsIconOutlined />
          ) : (
            <SettingsIcon />
          )
        }
        sx={styles}
        onClick={handleOpenDefaults}
        data-testid={`${testIdLabel}-button`}
      >
        {content}
      </Button>
    </Box>
  );
};

export default React.memo(SetFieldDefaults);
